import {
  Buttons,
  CancelButton,
  Drawer,
  HoursProgress,
  MultilineText,
  RevenueProgress,
  Stack,
  Tab,
  Tabs,
  Tag,
  Tags,
  TaskTypeIcon,
} from '~/components';
import ReadForm from '~/components/read-only/ReadForm';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import { useApi, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import projectTaskStatuses from '~/lookups/project-task-statuses';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';
import ProjectTaskHistory from './ProjectTaskHistory';

const TagContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding-left: 0.625em;
  padding-right: 2.5em;
  max-width: 100%;
  background-color: ${colors.grey5};
  border: solid 1px ${colors.grey10};
  border-radius: 0.3125rem;
`;

const TagItem = styled.div`
  border-radius: 0.3125rem;
  background-color: ${colors.grey10};
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  cursor: default;
  margin: 0.5rem 0.25rem 0.25rem 1rem;
`;

const TagPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0.625rem;
  margin-left: 1px;
  padding: 0 0.25rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
  background-color: ${colors.white};
  border-radius: 0.3125rem;
  transform: translateY(-50%);
  transition: opacity 100ms;
  pointer-events: none;
`;

const TaskName = styled.span`
  display: flex;
  align-items: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 1.625rem;
  margin-bottom: 1.625rem;
`;

const RollupProgress = styled.div`
  legend > span:last-child {
    color: ${colors.grey55};
  }
`;

const RollupSubtitle = styled.div`
  margin-top: 1.5rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;

  &:first-child {
    margin-top: 0;
  }
`;

const RollupHighlights = styled.div`
  display: flex;
  margin-top: 1rem;
`;

const RollupHighlight = styled.div`
  display: flex;
  align-items: center;
  color: ${colors.grey55};
  font-size: 0.75rem;

  &:not(:first-child) {
    padding-left: 0.75rem;
  }

  &:not(:last-child) {
    padding-right: 0.75rem;
    border-right: 1px solid ${colors.grey10};
  }
`;

const RollupStatus = styled.span`
  width: 0.625rem;
  height: 0.625rem;
  margin-right: 0.5rem;
  background-color: ${({ $statusId }) =>
    ({ not_started: colors.warning, in_progress: colors.success, completed: colors.black })[$statusId] ||
    colors.grey40};
  border-radius: 50%;
`;

const RollupSubtaskTags = styled(Tags)`
  margin: 0.5rem -0.25rem 0;
  font-size: 0.75rem;
`;

const RollupSubtaskTag = styled(Tag)`
  background-color: ${colors.primary10};
`;

const Control = ({ visible = true, children }) => (visible ? <ReadForm.Control>{children}</ReadForm.Control> : null);

const Description = ({ visible = true, label, children }) =>
  visible ? <ReadTextbox label={label} value={children} /> : null;

function ProjectTaskGroupDetails({ task, project, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [tabIndex, setTabIndex] = useState(0);
  const [descendants, setDescendants] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).descendants();
      setDescendants(data);
    })();
  }, [api, workspace.id, project.id, task]);

  useDocumentTitle('Task Details');

  return (
    <Drawer title="Task Details" isOpen onClose={onClose}>
      {(closeDrawer) => (
        <Stack>
          <Tabs selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
            <Tab>Task</Tab>
            {task.permissions.manage && <Tab>History</Tab>}
          </Tabs>

          <Content>
            {
              [
                <>
                  <ReadForm.Section title="Basic Information">
                    <Control>
                      <Description label="Task Name">
                        <TaskName>
                          <TaskTypeIcon icon={task.taskType.icon} color={task.taskType.color} />
                          {task.name}
                        </TaskName>
                      </Description>
                    </Control>

                    <Control visible={!!task.description}>
                      <Description label="Notes">
                        <MultilineText value={task.description} />
                      </Description>
                    </Control>

                    <Control visible={!!task.parent}>
                      <Description label="Parent Task">{task.parent?.name}</Description>
                    </Control>

                    <Control visible={!!task.code}>
                      <Description label="Task Code">{task.code}</Description>
                    </Control>

                    <Control visible={task.tags.length > 0}>
                      <TagContainer>
                        {task.tags.map((tag) => (
                          <TagItem key={tag.id}>{tag.name}</TagItem>
                        ))}
                        <TagPlaceholder>Tags</TagPlaceholder>
                      </TagContainer>
                    </Control>
                  </ReadForm.Section>

                  <ReadForm.Section title="Summary">
                    <div>
                      <RollupProgress>
                        <RollupSubtitle>Hours</RollupSubtitle>
                        <HoursProgress worked={task.hours} estimated={task.budgetHours} isRollup />

                        {task.permissions.viewRates && task.permissions.viewRevenue && project.isBillable && (
                          <>
                            <RollupSubtitle>Revenue</RollupSubtitle>
                            <RevenueProgress
                              earned={task.rollupRevenue}
                              budgeted={task.budgetRevenue}
                              currency={project.currency}
                              isRollup
                            />
                          </>
                        )}
                      </RollupProgress>

                      <RollupSubtitle>Details</RollupSubtitle>
                      <RollupHighlights>
                        {task.taskType && (
                          <RollupHighlight>
                            <TaskTypeIcon icon={task.taskType.icon} color={task.taskType.color} />
                            {task.taskType.name}
                          </RollupHighlight>
                        )}
                        {descendants.length > 0 && (
                          <RollupHighlight>
                            {descendants.length} {pluralize('task', descendants.length)}
                          </RollupHighlight>
                        )}
                        <RollupHighlight>
                          <RollupStatus $statusId={task.statusId} />
                          {projectTaskStatuses[task.statusId]?.name}
                        </RollupHighlight>
                        {(task.start || task.end) && (
                          <RollupHighlight>
                            {task.start && task.end ? (
                              <span>
                                {moment(task.start).format(dateFormats.compactDate)} –{' '}
                                {moment(task.end).format(dateFormats.compactDate)}
                              </span>
                            ) : task.start ? (
                              <span>Starts: {moment(task.start).format(dateFormats.compactDate)}</span>
                            ) : (
                              <span>Ends: {moment(task.end).format(dateFormats.compactDate)}</span>
                            )}
                          </RollupHighlight>
                        )}
                      </RollupHighlights>

                      {descendants.length > 0 && (
                        <>
                          <RollupSubtitle>Subtasks</RollupSubtitle>
                          <RollupSubtaskTags>
                            {descendants.map((descendant) => (
                              <RollupSubtaskTag key={descendant.id}>
                                <TaskTypeIcon icon={descendant.taskType?.icon} color={descendant.taskType?.color} />
                                {descendant.name}
                              </RollupSubtaskTag>
                            ))}
                          </RollupSubtaskTags>
                        </>
                      )}
                    </div>
                  </ReadForm.Section>
                </>,
                <>
                  <ProjectTaskHistory projectId={project.id} taskId={task.id} />
                </>,
              ][tabIndex]
            }
          </Content>

          <Drawer.Actions>
            <Buttons align="right">
              <CancelButton onClick={closeDrawer}>Close</CancelButton>
            </Buttons>
          </Drawer.Actions>
        </Stack>
      )}
    </Drawer>
  );
}

export default ProjectTaskGroupDetails;
